import React, { useState, useEffect, useCallback } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import apiService from '../../shared/services/ApiService';
import '../../css/reportes.css';

const WORK_TYPE_LABELS = {
    trimmer: 'Trimmer',
    tree: 'Árbol',
    ornamental: 'Ornamental',
    mixed: 'Mixto',
    other: 'Otro'
};

const STATUS_COLORS = {
    Pendiente: '#EF5BA1',
    Proceso: '#F39C12',
    Listo: '#27AE60'
};

const CHART_COLORS = ['#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#34495e'];

const ReportesPage = () => {
    const [reportData, setReportData] = useState(null);
    const [brigades, setBrigades] = useState([]);
    const [circunscripciones, setCircunscripciones] = useState([]);
    const [circMap, setCircMap] = useState({});
    const [loading, setLoading] = useState(false);

    // Filters
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [selectedBrigades, setSelectedBrigades] = useState([]);
    const [workTypeFilter, setWorkTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [circunscripcionFilter, setCircunscripcionFilter] = useState('all');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;

    useEffect(() => {
        fetchBrigades();
        fetchCircunscripciones();
    }, []);

    const fetchBrigades = async () => {
        try {
            const response = await apiService.get('/api/brigadas');
            setBrigades(response.data);
        } catch (err) {
            console.error('Error fetching brigades:', err);
        }
    };

    const fetchCircunscripciones = async () => {
        try {
            const response = await apiService.get('/api/circunscripciones');
            setCircunscripciones(response.data);
            const map = {};
            response.data.forEach(c => { map[c._id] = c.name; });
            setCircMap(map);
        } catch (err) {
            console.error('Error fetching circunscripciones:', err);
        }
    };

    // Resolver nombre de circunscripcion (ID → nombre)
    // eslint-disable-next-line no-unused-vars
    const resolveCircName = (circId) => {
        if (!circId) return 'Sin definir';
        return circMap[circId] || circId;
    };

    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (fechaInicio) params.append('fechaInicio', fechaInicio);
            if (fechaFin) params.append('fechaFin', fechaFin);
            if (selectedBrigades.length > 0) params.append('brigadeIds', selectedBrigades.join(','));
            if (workTypeFilter !== 'all') params.append('workType', workTypeFilter);
            if (statusFilter !== 'all') params.append('status', statusFilter);
            if (circunscripcionFilter !== 'all') {
                // Enviar el ID de la circunscripcion al backend
                const circId = circunscripciones.find(c => c.name === circunscripcionFilter)?._id || circunscripcionFilter;
                params.append('circunscripcion', circId);
            }

            const response = await apiService.get(`/api/reportes/general?${params.toString()}`);
            const data = response.data;

            // Resolver IDs de circunscripcion a nombres
            if (data.sectors) {
                data.sectors = data.sectors.map(s => ({
                    ...s,
                    circunscripcion: circMap[s.circunscripcion] || s.circunscripcion || 'Sin definir'
                }));
            }
            if (data.byCircunscripcion) {
                const resolved = {};
                Object.entries(data.byCircunscripcion).forEach(([key, val]) => {
                    const name = circMap[key] || key;
                    resolved[name] = val;
                });
                data.byCircunscripcion = resolved;
            }
            if (data.uniqueCircunscripciones) {
                data.uniqueCircunscripciones = data.uniqueCircunscripciones.map(c => circMap[c] || c);
            }

            setReportData(data);
            setCurrentPage(1);
        } catch (err) {
            console.error('Error fetching report:', err);
        } finally {
            setLoading(false);
        }
    }, [fechaInicio, fechaFin, selectedBrigades, workTypeFilter, statusFilter, circunscripcionFilter, circMap, circunscripciones]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const toggleBrigade = (id) => {
        setSelectedBrigades(prev =>
            prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]
        );
    };

    // Pagination
    const paginatedSectors = reportData?.sectors?.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    ) || [];
    const totalPages = reportData ? Math.ceil((reportData.sectors?.length || 0) / itemsPerPage) : 0;

    // Chart data
    const statusChartData = reportData ? Object.entries(reportData.byStatus).map(([name, value]) => ({
        name, value
    })) : [];

    const brigadeChartData = reportData ? Object.entries(reportData.byBrigade).map(([name, data]) => ({
        name: name.length > 15 ? name.substring(0, 15) + '...' : name,
        Listo: data.Listo,
        Proceso: data.Proceso,
        Pendiente: data.Pendiente
    })) : [];

    const workTypeChartData = reportData ? Object.entries(reportData.byWorkType).map(([name, value]) => ({
        name: WORK_TYPE_LABELS[name] || name, value
    })) : [];

    // Export functions
    const generatePDF = () => {
        if (!reportData) return;
        const doc = new jsPDF();
        const pw = doc.internal.pageSize.getWidth();

        // Header
        doc.setFillColor(0, 89, 168);
        doc.rect(0, 0, pw, 35, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text('Reporte General de Sectores', pw / 2, 22, { align: 'center' });

        doc.setTextColor(0, 0, 0);
        let y = 45;

        // Filters applied
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const filters = [];
        if (fechaInicio) filters.push(`Desde: ${fechaInicio}`);
        if (fechaFin) filters.push(`Hasta: ${fechaFin}`);
        if (workTypeFilter !== 'all') filters.push(`Tipo: ${WORK_TYPE_LABELS[workTypeFilter]}`);
        if (statusFilter !== 'all') filters.push(`Estado: ${statusFilter}`);
        if (filters.length > 0) {
            doc.text(`Filtros: ${filters.join(' | ')}`, 15, y);
            y += 8;
        }
        doc.text(`Fecha de generación: ${new Date().toLocaleDateString('es-ES')}`, 15, y);
        y += 12;

        // Summary boxes
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Resumen', 15, y);
        y += 8;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Total sectores: ${reportData.total}`, 15, y);
        doc.text(`Pendientes: ${reportData.byStatus.Pendiente}`, 75, y);
        doc.text(`En proceso: ${reportData.byStatus.Proceso}`, 130, y);
        y += 6;
        doc.text(`Completados: ${reportData.byStatus.Listo}`, 15, y);
        doc.text(`Progreso: ${reportData.completionRate}%`, 75, y);
        y += 12;

        // By brigade
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Por Brigada', 15, y);
        y += 8;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Brigada', 15, y);
        doc.text('Total', 90, y);
        doc.text('Pend.', 110, y);
        doc.text('Proc.', 130, y);
        doc.text('Listo', 150, y);
        y += 2;
        doc.setDrawColor(200);
        doc.line(15, y, 170, y);
        y += 5;

        doc.setFont('helvetica', 'normal');
        Object.entries(reportData.byBrigade).forEach(([name, data]) => {
            if (y > 270) { doc.addPage(); y = 20; }
            doc.text(name.substring(0, 30), 15, y);
            doc.text(String(data.total), 90, y);
            doc.text(String(data.Pendiente), 110, y);
            doc.text(String(data.Proceso), 130, y);
            doc.text(String(data.Listo), 150, y);
            y += 6;
        });
        y += 8;

        // Sectors table
        if (y > 240) { doc.addPage(); y = 20; }
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Detalle de Sectores', 15, y);
        y += 8;

        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('Sector', 15, y);
        doc.text('Estado', 65, y);
        doc.text('Tipo', 90, y);
        doc.text('Brigada', 120, y);
        doc.text('Progreso', 165, y);
        y += 2;
        doc.line(15, y, pw - 15, y);
        y += 5;

        doc.setFont('helvetica', 'normal');
        reportData.sectors.forEach(s => {
            if (y > 275) { doc.addPage(); y = 20; }
            doc.text((s.name || '').substring(0, 20), 15, y);
            doc.text(s.status, 65, y);
            doc.text(WORK_TYPE_LABELS[s.workType] || s.workType, 90, y);
            doc.text((s.brigade || '').substring(0, 18), 120, y);
            doc.text(`${s.progress}%`, 165, y);
            y += 5;
        });

        doc.save(`reporte_general_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const generateExcel = () => {
        if (!reportData) return;

        // Sheet 1: Sectors
        const sectorsData = reportData.sectors.map(s => ({
            'Sector': s.name,
            'Estado': s.status,
            'Tipo de Trabajo': WORK_TYPE_LABELS[s.workType] || s.workType,
            'Brigada': s.brigade,
            'Circunscripción': s.circunscripcion || 'N/A',
            'Fecha Programada': s.scheduledDate ? new Date(s.scheduledDate).toLocaleDateString('es-ES') : 'N/A',
            'Puntos Totales': s.totalCoords,
            'Puntos Completados': s.completedCoords,
            'Progreso (%)': s.progress
        }));
        const ws1 = XLSX.utils.json_to_sheet(sectorsData);

        // Sheet 2: Summary by brigade
        const brigadeData = Object.entries(reportData.byBrigade).map(([name, data]) => ({
            'Brigada': name,
            'Total': data.total,
            'Pendientes': data.Pendiente,
            'En Proceso': data.Proceso,
            'Completados': data.Listo,
            'Completado (%)': data.total > 0 ? Math.round((data.Listo / data.total) * 100) : 0
        }));
        const ws2 = XLSX.utils.json_to_sheet(brigadeData);

        // Sheet 3: Summary by circunscripcion
        const circData = Object.entries(reportData.byCircunscripcion).map(([name, data]) => ({
            'Circunscripción': name,
            'Total': data.total,
            'Pendientes': data.Pendiente,
            'En Proceso': data.Proceso,
            'Completados': data.Listo
        }));
        const ws3 = XLSX.utils.json_to_sheet(circData);

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws1, 'Sectores');
        XLSX.utils.book_append_sheet(wb, ws2, 'Por Brigada');
        XLSX.utils.book_append_sheet(wb, ws3, 'Por Circunscripción');
        XLSX.writeFile(wb, `reporte_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    return (
        <div className="reportes-container">
            {/* Header */}
            <div className="reportes-header">
                <div className="reportes-header-content">
                    <h1 className="reportes-title">Reportes</h1>
                    <p className="reportes-subtitle">Consulta y exporta reportes detallados</p>
                </div>
                <div className="reportes-export-actions">
                    <button className="report-export-btn pdf" onClick={generatePDF} disabled={!reportData || reportData.total === 0}>
                        PDF
                    </button>
                    <button className="report-export-btn excel" onClick={generateExcel} disabled={!reportData || reportData.total === 0}>
                        Excel
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="reportes-filters">
                <div className="filter-row">
                    <div className="filter-item">
                        <label>Desde</label>
                        <input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
                    </div>
                    <div className="filter-item">
                        <label>Hasta</label>
                        <input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} />
                    </div>
                    <div className="filter-item">
                        <label>Tipo de Trabajo</label>
                        <select value={workTypeFilter} onChange={e => setWorkTypeFilter(e.target.value)}>
                            <option value="all">Todos</option>
                            <option value="trimmer">Trimmer</option>
                            <option value="tree">Árbol</option>
                            <option value="ornamental">Ornamental</option>
                            <option value="mixed">Mixto</option>
                        </select>
                    </div>
                    <div className="filter-item">
                        <label>Estado</label>
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                            <option value="all">Todos</option>
                            <option value="Pendiente">Pendiente</option>
                            <option value="Proceso">En Proceso</option>
                            <option value="Listo">Listo</option>
                        </select>
                    </div>
                    {reportData?.uniqueCircunscripciones?.length > 0 && (
                        <div className="filter-item">
                            <label>Circunscripción</label>
                            <select value={circunscripcionFilter} onChange={e => setCircunscripcionFilter(e.target.value)}>
                                <option value="all">Todas</option>
                                {reportData.uniqueCircunscripciones.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                <div className="filter-brigades">
                    <label>Brigadas:</label>
                    <div className="brigade-chips">
                        {brigades.map(b => (
                            <button
                                key={b._id}
                                className={`brigade-chip ${selectedBrigades.includes(b._id) ? 'active' : ''}`}
                                onClick={() => toggleBrigade(b._id)}
                            >
                                {b.name}
                            </button>
                        ))}
                        {selectedBrigades.length > 0 && (
                            <button className="brigade-chip clear" onClick={() => setSelectedBrigades([])}>
                                Limpiar
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="reportes-loading">
                    <div className="spinner-reportes"></div>
                    <p>Generando reporte...</p>
                </div>
            ) : reportData ? (
                <>
                    {/* Summary cards */}
                    <div className="reportes-summary">
                        <div className="summary-card">
                            <h3>{reportData.total}</h3>
                            <p>Total Sectores</p>
                        </div>
                        <div className="summary-card pendiente">
                            <h3>{reportData.byStatus.Pendiente}</h3>
                            <p>Pendientes</p>
                        </div>
                        <div className="summary-card proceso">
                            <h3>{reportData.byStatus.Proceso}</h3>
                            <p>En Proceso</p>
                        </div>
                        <div className="summary-card listo">
                            <h3>{reportData.byStatus.Listo}</h3>
                            <p>Completados</p>
                        </div>
                        <div className="summary-card progreso">
                            <h3>{reportData.completionRate}%</h3>
                            <p>Progreso General</p>
                        </div>
                    </div>

                    {/* Charts */}
                    <div className="reportes-charts">
                        <div className="chart-card">
                            <h3>Estado de Sectores</h3>
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie data={statusChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                                        {statusChartData.map((entry, i) => (
                                            <Cell key={i} fill={STATUS_COLORS[entry.name] || CHART_COLORS[i]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="chart-card">
                            <h3>Por Brigada</h3>
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={brigadeChartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="Listo" fill="#27AE60" stackId="a" />
                                    <Bar dataKey="Proceso" fill="#F39C12" stackId="a" />
                                    <Bar dataKey="Pendiente" fill="#EF5BA1" stackId="a" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {workTypeChartData.length > 1 && (
                            <div className="chart-card">
                                <h3>Tipo de Trabajo</h3>
                                <ResponsiveContainer width="100%" height={250}>
                                    <PieChart>
                                        <Pie data={workTypeChartData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                                            {workTypeChartData.map((_, i) => (
                                                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>

                    {/* Table */}
                    <div className="reportes-table-section">
                        <h3>Detalle de Sectores ({reportData.sectors?.length || 0})</h3>
                        <div className="reportes-table-wrapper">
                            <table className="reportes-table">
                                <thead>
                                    <tr>
                                        <th>Sector</th>
                                        <th>Estado</th>
                                        <th>Tipo</th>
                                        <th>Brigada</th>
                                        <th>Circunscripción</th>
                                        <th>Fecha</th>
                                        <th>Progreso</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedSectors.map(s => (
                                        <tr key={s._id}>
                                            <td>{s.name}</td>
                                            <td>
                                                <span className={`status-pill ${s.status.toLowerCase()}`}>
                                                    {s.status}
                                                </span>
                                            </td>
                                            <td>{WORK_TYPE_LABELS[s.workType] || s.workType}</td>
                                            <td>{s.brigade}</td>
                                            <td>{s.circunscripcion || '-'}</td>
                                            <td>{s.scheduledDate ? new Date(s.scheduledDate).toLocaleDateString('es-ES') : '-'}</td>
                                            <td>
                                                <div className="progress-cell">
                                                    <div className="progress-bar-mini">
                                                        <div className="progress-fill-mini" style={{ width: `${s.progress}%` }}></div>
                                                    </div>
                                                    <span>{s.progress}%</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="reportes-pagination">
                                <button
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(p => p - 1)}
                                >Anterior</button>
                                <span>Página {currentPage} de {totalPages}</span>
                                <button
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(p => p + 1)}
                                >Siguiente</button>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="reportes-empty">
                    <p>No hay datos disponibles</p>
                </div>
            )}
        </div>
    );
};

export default ReportesPage;
